// Утилітні функції для роботи з уроками та зображеннями
// client/src/utils/lessonUtils.js

import { lessonsAPI } from '../services/api';

/**
 * Створює FormData для уроку з усіма необхідними полями
 * @param {Object} lessonData - дані уроку
 * @param {File} imageFile - файл зображення (опціонально)
 * @returns {FormData}
 */
export const createLessonFormData = (lessonData, imageFile = null) => {
  const formData = new FormData();
  
  // Додаємо всі дані як JSON в поле 'data'
  const dataToSend = {
    title: lessonData.title || '',
    description: lessonData.description || '',
    content: lessonData.content || '',
    type: lessonData.type || 'text',
    contentType: lessonData.contentType || 'standard',
    videoUrl: lessonData.videoUrl || '',
    isDraft: lessonData.isDraft || false,
    contentBlocks: lessonData.contentBlocks || [],
    resources: lessonData.resources || [],
    practiceExercises: lessonData.practiceExercises || []
  };
  
  formData.append('data', JSON.stringify(dataToSend));
  
  // Зображення - використовуємо imageFile якщо є, інакше image URL
  if (imageFile) {
    formData.append('image', imageFile);
  } else if (lessonData.image && !lessonData.imagePreview) {
    // Якщо є URL зображення, але немає файлу
    formData.append('image', lessonData.image);
  }
  
  return formData;
};

/**
 * Створює новий урок з зображенням
 * @param {Object} lessonData - дані уроку (може містити imageFile)
 * @returns {Promise}
 */
export const createLessonWithImage = async (lessonData) => {
  const formData = createLessonFormData(lessonData, lessonData.imageFile);
  return await lessonsAPI.createWithImage(formData);
};

/**
 * Оновлює існуючий урок з зображенням
 * @param {string} lessonId - ID уроку
 * @param {Object} lessonData - дані уроку (може містити imageFile)
 * @returns {Promise}
 */
export const updateLessonWithImage = async (lessonId, lessonData) => {
  const formData = createLessonFormData(lessonData, lessonData.imageFile);
  return await lessonsAPI.updateWithImage(lessonId, formData);
};

/**
 * Валідує файл зображення
 * @param {File} file - файл для валідації
 * @returns {Object} - { isValid: boolean, error?: string }
 */
export const validateImageFile = (file) => {
  if (!file) {
    return { isValid: false, error: 'Файл не вибрано' };
  }
  
  if (!file.type.startsWith('image/')) {
    return { isValid: false, error: 'Будь ласка, виберіть файл зображення' };
  }
  
  // Перевірка розміру (5MB)
  if (file.size > 5 * 1024 * 1024) {
    return { isValid: false, error: 'Розмір файлу не повинен перевищувати 5MB' };
  }
  
  return { isValid: true };
};

/**
 * Зберігає урок (створює або оновлює) з урахуванням зображення
 * @param {Object} lessonData - дані уроку (може містити imageFile)
 * @returns {Promise}
 */
export const saveLesson = async (lessonData) => {
  let response;
  const hasImageToUpload = lessonData.imageFile || (lessonData.imagePreview && !lessonData.image);
  
  // Маппим professions - берем только ID
  if (Array.isArray(lessonData.professions)) {
    lessonData.professions = lessonData.professions.map(prof => 
      typeof prof === 'object' && prof.id ? prof.id : prof
    );
  }
  
  // Маппим skills - берем только ID
  if (Array.isArray(lessonData.skills)) {
    lessonData.skills = lessonData.skills.map(skill => 
      typeof skill === 'object' && skill.id ? skill.id : skill
    );
  }
  
  if (lessonData._id) {
    // Оновлення існуючого уроку
    if (hasImageToUpload) {
      response = await updateLessonWithImage(lessonData._id, lessonData);
    } else {
      response = await lessonsAPI.update(lessonData._id, lessonData);
    }
  } else {
    // Створення нового уроку
    if (hasImageToUpload) {
      response = await createLessonWithImage(lessonData);
    } else {
      response = await lessonsAPI.create(lessonData);
    }
  }
  
  // Очищаємо imagePreview після успішного збереження
  if (response.data.success && lessonData.imagePreview) {
    URL.revokeObjectURL(lessonData.imagePreview);
  }
  
  return response;
};

/**
 * Очищає тимчасові поля після збереження уроку
 * @param {Object} savedLesson - збережений урок з сервера
 * @returns {Object} - очищений урок
 */
export const cleanLessonAfterSave = (savedLesson) => {
  return {
    ...savedLesson,
    imagePreview: null,
    imageFile: null
  };
};
